import React, { useEffect, useRef, useState } from 'react';

import uuidV4 from 'uuid/v4';

import { partnerLogoMap as PARTNER_MAP } from 'wanderu-assets';
import WanderuErrorBoundary from 'wanderu-component-error-boundary';
import Intercom from 'wanderu-component-intercom';
import PartnerLogo from 'wanderu-component-partner-logo';
import {
    Layout,
} from 'wanderu-components';
import { SearchBanner, liveCheckEvents } from 'wanderu-components-search';
import { localize } from 'wanderu-i18n';
import { fireCheckoutEvent } from 'wanderu-service-analytics';

import {
    DEPART_DIRECTION,
    RETURN_DIRECTION,
    TRIP_SUMMARY,
} from '../../constants';
import LiveCheckStatus from './LiveCheckStatus/LiveCheckStatus';
import ReturnButton from './ReturnButton/ReturnButton';
import UnavailableModal from './UnavailableModal/UnavailableModal';
import events from './events/TripSummaryEvents';
import { buildReturnUrl, createSearchUrl } from './utils';
import BookButton from '../../containers/BookButton/BookButton';
import CarrierPolicies from '../../containers/CarrierPolicies';
import TravelerInfo from '../../containers/TravelerInfo/TravelerInfo';
import TripSummaryLine from '../../containers/TripSummaryLine/TripSummaryLine';

import styles from './TripSummary.scss';

import type { TripSummaryProps } from './types';

const TripSummary = (props: TripSummaryProps) => {
    const {
        accountEmail,
        affiliateId,
        allTripIds,
        carrierProviderGroup,
        currency,
        deeplinkRoundtrip,
        destinationPlaceId,
        forceModalClose,
        i18nMoney,
        identityId,
        itineraryId,
        liveCheckData = {},
        liveChecksCompleted,
        liveChecksInProgress,
        locale = 'en-us',
        onUnavailableReturnClick,
        originPlaceId,
        outboundArrive = {},
        outboundDepart = {},
        outboundTripError,
        outboundTripId,
        outboundTripIds,
        outboundTrips = [],
        requestedWheelchairs,
        returnTripError,
        returnTripId,
        returnTripIds,
        returnTrips = [],
        searchQueries,
        seatInfo = {},
        seats,
        showSingleBookButton,
        showTotalPrice,
        t,
        trackingId,
        tripFareNames = {},
        tripItineraryId,
        tripPrices = [],
        wheelchairs,
    } = props;

    const {
        error = [],
        nativeBookingEnabled = [],
        notFound = [],
        priceDown = [],
        showLiveCheckMessage,
        total = 0,
        unavailable = [],
    } = liveCheckData;

    const [haveFiredDeeplinkCheckoutEvent, setHaveFiredDeeplinkCheckoutEvent] = useState(false);
    const [tripFareEventFired, setTripFareEventFired] = useState(false);

    const itineraryUUID = useRef(uuidV4());

    useEffect(() => {
        events.firePageLoadEvent(outboundTripId, returnTripId);

        if (liveChecksCompleted && Object.keys(tripFareNames).length) {
            events.loadTripSummaryFareNames(tripFareNames);
            setTripFareEventFired(true);
        }

        return () => {
            events.endMultiBookingTransaction();
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (liveChecksCompleted) {
            if (!tripFareEventFired && Object.keys(tripFareNames).length) {
                events.loadTripSummaryFareNames(tripFareNames);
                setTripFareEventFired(true);
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [liveChecksCompleted]);

    useEffect(() => {
        if (showLiveCheckMessage) {
            liveCheckEvents.liveCheckResultViewed({
                funnelStage: 'trip_summary',
                itineraryId,
                liveCheckData,
                seats,
                trips: [...outboundTrips, ...returnTrips],
                uiViewed: 'banner', // check if it's triggering both banner and unavailable modal
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showLiveCheckMessage]);

    const onBookClick = (isDeeplink: boolean, tripId: string, url: string) => {
        const totalPrice = i18nMoney(tripPrices).getTotalPrice();
        const allTrips = [...outboundTrips, ...returnTrips];
        const itinerary = returnTripId
            ? `${outboundTripId}||${returnTripId}`
            : outboundTripId;

        if (!haveFiredDeeplinkCheckoutEvent) {
            const hasDeeplink = nativeBookingEnabled.length < total;

            fireCheckoutEvent({
                currency,
                email: accountEmail,
                identityId,
                itineraryId,
                linkType: hasDeeplink
                    ? 'deeplink'
                    : 'native',
                locale,
            });

            setHaveFiredDeeplinkCheckoutEvent(true);
        }

        if (showSingleBookButton) {
            events.singleBookingSubmitted({
                affiliateId,
                currency,
                isNativeData: nativeBookingEnabled,
                itinerary,
                itineraryUUID: itineraryUUID.current,
                seats,
                totalPrice,
                trackingId,
                trips: allTrips,
                url,
            });
        } else {
            events.recordMultiBookingTransaction({
                affiliateId,
                allTripIds,
                currency,
                isNativeData: liveCheckData.nativeBookingEnabled,
                itinerary,
                itineraryUUID: itineraryUUID.current,
                seats,
                totalPrice,
                trackingId,
                trip: allTrips.find((trip) => trip.tripId === tripId),
                url,
            });
        }
    };

    const onChangeTrip = (e: React.MouseEvent): void => {
        e.stopPropagation();
        events.changeTripButtonClicked({
            tripId: outboundTripId,
        });
    };

    const fireUnavailableReturnClick = (e: React.MouseEvent): void => {
        [...notFound, ...unavailable].forEach(
            (tripId) => onUnavailableReturnClick?.(tripId)
        );

        onChangeTrip(e);
    };

    if (outboundTripError || returnTripError) {
        const changeTripUrl = outboundTripError
            ? createSearchUrl(props, DEPART_DIRECTION)
            : createSearchUrl(props, RETURN_DIRECTION);

        return (
            <UnavailableModal
                changeTripUrl={changeTripUrl}
                onClick={onChangeTrip}
            />
        );
    }

    const {
        cityName: departCityName = '',
        region: departRegion = '',
    } = outboundDepart;
    const {
        cityName: arriveCityName = '',
        localizedDateTime: arriveDate = '',
        region: arriveRegion = '',
    } = outboundArrive;

    const language = locale.split('-')[0];
    const originText = `${departCityName} ${departRegion}`;
    const destinationText = `${arriveCityName} ${arriveRegion}`;
    const journeyPrice = i18nMoney.format(i18nMoney(tripPrices).getTotalPrice());
    const showUnavailableModal = !!unavailable.length || !!notFound.length || !!error.length;
    const unavailableTrips = [
        ...unavailable,
        ...notFound,
        ...error,
    ];
    const showIndividualBookButtons = liveChecksCompleted && !showSingleBookButton;

    const liveCheckIds = {
        affiliateId,
        allTripIds,
        trackingId,
    };

    const tripSummaries = () => {
        return (
            <>
                {!!outboundTripId && (
                    <TripSummaryLine
                        arriveDate={arriveDate}
                        className={styles.tripLine}
                        destinationText={destinationText}
                        direction={DEPART_DIRECTION}
                        forceModalClose={forceModalClose}
                        liveCheckIds={liveCheckIds}
                        liveChecksInProgress={liveChecksInProgress}
                        onBookClick={onBookClick}
                        originText={originText}
                        seats={seats}
                        showIndividualBookButtons={showIndividualBookButtons}
                        showReturnTripAdder={!returnTripId}
                        showTotalPrice={showTotalPrice}
                        tripId={outboundTripId}
                        wheelchairs={wheelchairs}
                    />
                )}

                {!!returnTripId && (
                    <TripSummaryLine
                        changeTripUrl={createSearchUrl(props, RETURN_DIRECTION)}
                        className={styles.tripLine}
                        direction={RETURN_DIRECTION}
                        forceModalClose={forceModalClose}
                        liveCheckIds={liveCheckIds}
                        liveChecksInProgress={liveChecksInProgress}
                        onBookClick={onBookClick}
                        seats={seats}
                        showIndividualBookButtons={showIndividualBookButtons}
                        showTotalPrice={showTotalPrice}
                        tripId={returnTripId}
                        wheelchairs={wheelchairs}
                    />
                )}
            </>
        );
    };

    return (
        <>
            <SearchBanner
                bannerText={t('trip-summary')}
                changeDepartTripUrl={createSearchUrl(props, DEPART_DIRECTION)}
                changeReturnTripUrl={createSearchUrl(props, RETURN_DIRECTION)}
                departDate={outboundTrips?.[0]?.localizedDepartDateTime}
                locale={locale}
                outboundTripId={outboundTripId}
                returnDate={returnTrips?.[0]?.localizedDepartDateTime || ''}
                returnTripId={returnTripId}
                step={TRIP_SUMMARY}
            />

            {showUnavailableModal && liveChecksCompleted && (
                <UnavailableModal
                    changeTripUrl={buildReturnUrl(props, unavailableTrips)}
                    onClick={fireUnavailableReturnClick}
                />
            )}

            <WanderuErrorBoundary
                backButtonText={t('back-homepage-button')}
                backButtonUrl="/"
                scope="TripSummaryComponent"
            >
                <Layout className={styles.container}>

                    <section className={styles.tripLinesContainer}>
                        <LiveCheckStatus
                            isLowerPrice={!!priceDown.length}
                            liveCheckData={liveCheckData}
                            outboundTripIds={outboundTripIds}
                            returnTripIds={returnTripIds}
                        />

                        {tripSummaries()}

                        {language.toLowerCase() === 'en'
                                && <CarrierPolicies
                                    className={styles.hideLarge}
                                    tripIds={allTripIds}
                                />
                        }

                        {showSingleBookButton && (
                            <>
                                <div className={styles.stickySpacer} />
                                <div className={styles.stickyContainer}>
                                    {!returnTripId && (
                                        <ReturnButton
                                            arriveDate={arriveDate}
                                            className={`${styles.stickyBookButton} ${styles.bookButton}`}
                                            destinationPlaceId={destinationPlaceId}
                                            destinationText={destinationText}
                                            originPlaceId={originPlaceId}
                                            originText={originText}
                                            tripId={outboundTripId}
                                        />
                                    )}
                                    <BookButton
                                        buttonContentsClassName={
                                            styles.stickyBookButtonContents
                                        }
                                        className={
                                            `${styles.stickyBookButton} ${styles.bookButton}`
                                        }
                                        deeplink={deeplinkRoundtrip}
                                        onClick={onBookClick}
                                        price={journeyPrice}
                                        searchQueries={searchQueries}
                                        seats={seats}
                                        tripIds={allTripIds}
                                        tripItineraryId={tripItineraryId}
                                        wheelchairs={wheelchairs}
                                        show
                                    />
                                </div>
                            </>
                        )}
                    </section>

                    <aside className={styles.sidebar}>
                        <LiveCheckStatus
                            isLowerPrice={!!priceDown.length}
                            liveCheckData={liveCheckData}
                            outboundTripIds={outboundTripIds}
                            returnTripIds={returnTripIds}
                            useMobileStyling
                        />
                        <TravelerInfo
                            maxSeats={seatInfo.maxSeats}
                            maxWheelchairs={seatInfo.maxWheelchairs}
                            nonAdaCarriers={seatInfo.nonAdaCarriers}
                            outboundTripId={outboundTripId}
                            requestedWheelchairs={requestedWheelchairs}
                            returnTripId={returnTripId}
                            seats={seats}
                            wheelchairs={wheelchairs}
                        />

                        {language.toLowerCase() === 'en'
                                && <CarrierPolicies
                                    className={styles.hideSmall}
                                    isOneWay={!returnTripId}
                                    itineraryId={tripItineraryId}
                                    price={journeyPrice}
                                    tripIds={allTripIds}
                                />
                        }
                        {!!carrierProviderGroup && (
                            <PartnerLogo
                                className={styles.partnerLogoDesktop}
                                partner={PARTNER_MAP[carrierProviderGroup]}
                                partnerId={carrierProviderGroup}
                                flights
                            />
                        )}
                    </aside>
                    <Intercom />
                </Layout>
            </WanderuErrorBoundary>
        </>
    );
};

export { TripSummary };
export default localize(TripSummary, ['t', 'i18nMoney'], 'baseApp');
